<template>
  <div class="ds-width-full">
    <CaseStudyPage
      v-if="contentType === 'caseStudy'"
      :case-study="caseStudy"
      :sidebar-tags="sidebarTags"
      :page-seo-data="pageSeoData"
      :parent-page-url="parentPageUrl"
    />
    <RetailReportPage
      v-if="contentType === 'retailReport'"
      :report="retailReport"
      :show-form="!isFormSubmitStateSuccess"
      @form-success="updateFormState"
    />
    <ArticlePage
      v-else-if="contentType === 'article'"
      :article="article"
      :page-seo-data="pageSeoData"
      :sidebar-tags="sidebarTags"
      :parent-page-url="parentPageUrl"
      :social-items="socialItems"
      :header-tag="headerTag"
      :newsletter-form-data="content.newsletterFormData || newsletterFormData"
    />
    <CampaignPage
      v-else-if="contentType === 'campaign'"
      :campaign="campaign"
      :title="content.title"
      :header-image="headerImage"
      header-media-orientation="right"
      :parent-page="parentPageUrl"
      :show-form="!isFormSubmitStateSuccess"
      @form-success="updateFormState"
    />
  </div>
</template>
<script setup>
import { format } from 'date-fns/format'
import queryContentKnowledgeHubCampaignsContentBlocks from '~/data-layer/campaigns/query-content-knowledge-hub-campaigns-content-blocks'
import FORM_NAME from '../../../core/constants/globalForm'
import { PageKnowledgeHubDetail } from '../../../core/data-layer/definitions/knowledgeHub'
import ArticlePage from '../../components/article/article-page.vue'
import CampaignPage from '../../components/campaigns/campaign-page.vue'
import CaseStudyPage from '../../components/case-study/case-study-page.vue'
import RetailReportPage from '../../components/retail-report/retail-report-page.vue'
import { knowledgeHubFilterMap } from '../../constants/filters'
import mapperContentKnowledgeHubCampaignsContentBlocks from '~/data-layer/campaigns/mapper-content-knowledge-hub-campaigns-content-blocks'
import mapperStructuredData from '~/data-layer/pages/page-knowledge-hub-detail/structured-data-mapper-page-knowledge-hub-detail'

const { labels, localeGlobalContentData } = useKnowledgeHubGlobalData()
const { queryContentful } = useQueries()
const { useStandardAsyncData, onBeforeAdyenData, onAfterAdyenData, content } = useKnowledgeHubPageData()
const { locale: localeRef } = useI18n()
const { isScreenLarge } = useScreen()
const { getLocaleIso, getI18nFormatterLocale } = useLocales()
const { setTheme } = useTheme()
const route = useRoute()
const store = useRoutesStore()
const { getUrl, getInternalUrl } = useRoutes(store.routes, localeRef)
const isPreview = useRuntimeConfig().public.preview
const siteUrl = useRuntimeConfig().public.siteUrl
const { getParentPath } = usePageUtils()
setTheme('light')

await onBeforeAdyenData({
  globalDataContentType: PageKnowledgeHubDetail.contentType
})

const isoLocale = getLocaleIso(localeRef.value)

const { data } = await useAsyncData('knowledge-hub-slug-' + route.params.slug + '-' + localeRef.value, async () => {
  // This callback is not fired when the slug is not recognized??
  const { pageData } = await useStandardAsyncData(PageKnowledgeHubDetail, { log: false })

  if (!pageData || !Object.keys(pageData).length) {
    return null
  }

  const pageType = pageData?.content?.pageContent?.type

  /**
   * @TODO(Team): Workaround for "Query too big" error, remove as soon as possible
   * We query all the contentBlocks that have been received by the pageContent
   * And then we map them here
   * Super dirty, but we had to split the queries because of GraphQL limitation on query size
   * Shall we move out from GraphQL for pages data?
   * Reference ticket for refactor:
   * @see https://youtrack.is.adyen.com/issue/WEB-4916
   */
  const campaignSysId = pageData.content?.pageContent?.sys?.id
  if (pageType === 'ContentKnowledgeHubCampaignsGuide' && campaignSysId) {
    const blocksResponse = await queryContentful(
      {
        query: queryContentKnowledgeHubCampaignsContentBlocks,
        variables: {
          preview: isPreview,
          locale: isoLocale,
          campaignId: campaignSysId
        }
      },
      { log: false }
    )

    const mapperOptions = {
      formatter: getI18nFormatterLocale,
      getUrl,
      getInternalUrl,
      locale: localeRef.value,
      labels: labels.value
    }

    const mappedData = mapperContentKnowledgeHubCampaignsContentBlocks(blocksResponse, mapperOptions)

    pageData.content.pageContent.contentBlocks = mappedData
  }

  const dateFnsLocale = getI18nFormatterLocale(localeRef.value)

  const dateFormatted =
    pageData.content?.date &&
    format(new Date(pageData.content?.date), isoLocale === 'en' ? 'MMMM d, yyyy' : 'd MMMM, yyyy', {
      locale: dateFnsLocale
    })
  const slugParent = getParentPath(route.path)

  // TODO - MIGRATE
  const { getRoute, getRouteByEntry } = useRoutes(store.routes, localeRef)
  const pageRoute = getRoute(route.params.slug)
  const articlesPageRoute = getRouteByEntry('3kcR7YrWAsdeQAgbxHghYA')?.route || '/articles'

  // Fallback Open Graph Image
  let headerImage = null
  if (pageType === 'ContentKnowledgeHubCampaignsGuide' && !pageData.content?.pageContent?.showFormInHeader) {
    // Needed for fallback logic on campaigns hero image not being populated
    /**
     * Zoom parameter ticket
     * @see https://youtrack.is.adyen.com/issue/WEB-4273
     * Campaigns Hero ticket
     * @see https://youtrack.is.adyen.com/issue/WEB-4624
     */

    // return vertical image to be displayed on the right
    const imageData = pageData.content?.image?.[0]
    if (!pageData.content?.fpZoom) {
      headerImage = imageData
    } else {
      const zoomParameter = `&fp_zoom=${pageData.content.fpZoom / 100}`
      if (imageData?.small?.src.includes('crop=fp') && !imageData?.small?.src.includes(zoomParameter)) {
        imageData.small.src += zoomParameter
      }

      if (imageData?.medium?.src.includes('crop=fp') && !imageData?.medium?.src.includes(zoomParameter)) {
        imageData.medium.src += zoomParameter
      }

      if (imageData?.large?.src.includes('crop=fp') && !imageData?.large?.src.includes(zoomParameter)) {
        imageData.large.src += zoomParameter
      }

      headerImage = imageData
    }
  } else if (pageType === 'ContentKnowledgeHubArticle') {
    if (pageData?.content.pageContent?.displayFullWidthImageHero && pageData?.content?.pageContent?.headerMedia?.[3]) {
      // default 16:9 image
      headerImage = pageData?.content?.pageContent?.headerMedia?.[3]
    } else if (pageData?.content?.pageContent?.headerMedia?.[0]) {
      // resized 3:4 image
      headerImage = pageData?.content?.pageContent?.headerMedia?.[0]
    }
  } else if (
    pageType === 'ContentKnowledgeHubCaseStudy' &&
    !Array.isArray(pageData?.content?.pageContent?.headerMedia)
  ) {
    if (pageData?.content?.pageContent?.headerMedia?.isImage) {
      headerImage = pageData?.content?.pageContent?.headerMedia
    } else if (pageData?.content?.pageContent?.headerMedia?.isVideo) {
      headerImage = pageData?.content.pageContent?.headerMedia.thumbnail
    }
    headerImage = pageData?.content?.pageContent?.headerMedia
  } else if (pageType === 'ContentKnowledgeHubRr23' && !Array.isArray(pageData?.content?.pageContent?.hero?.image)) {
    headerImage = pageData?.content?.pageContent?.hero?.image
  }

  let fallbackOgImage = headerImage?.medium?.src
  if (headerImage?.original?.extension === 'svg') {
    fallbackOgImage += '?format=png'
  }

  if (!pageData?.seo?.ogImage) {
    pageData.seo.ogImage = fallbackOgImage
  }

  return {
    pageData,
    dateFormatted,
    slugParent,
    articlesPageRoute,
    headerImage,
    pageRoute,
    minutesToRead: pageData?.content?.minutesToRead || 0,
    newsletterFormData: localeGlobalContentData.value?.globalForms[FORM_NAME.newsletterKnowledgeHub],
    // Structured data to be render as JSON-LD in page header
    structuredData: mapperStructuredData(pageData, { locale: localeRef.value, siteUrl })
  }
})

const isFormSubmitStateSuccess = ref(false)

const articlesPageRoute = computed(() => {
  return data.value?.articlesPageRoute
})

const pageRoute = computed(() => {
  return data.value?.pageRoute
})

const newsletterFormData = computed(() => {
  return data.value?.newsletterFormData || {}
})

const parentPageUrl = computed(() => {
  return data.value?.slugParent
})

const pageContent = computed(() => {
  return content.value.pageContent
})

const contentType = computed(() => {
  // Possible content types for KH Detail: ['contentKnowledgeHubArticle', 'contentKnowledgeHubRr23', 'contentKnowledgeHubCaseStudy', 'contentKnowledgeHubCampaignsGuide']
  if (content.value.pageContent?.type === 'ContentKnowledgeHubArticle') {
    return 'article'
  } else if (content.value.pageContent?.type === 'ContentKnowledgeHubCaseStudy') {
    return 'caseStudy'
  } else if (content.value.pageContent?.type === 'ContentKnowledgeHubRr23') {
    return 'retailReport'
  } else if (content.value.pageContent?.type === 'ContentKnowledgeHubCampaignsGuide') {
    return 'campaign'
  } else {
    return null
  }
})

const retailReport = computed(() => {
  return contentType.value === 'retailReport' ? pageContent.value : null
})

const article = computed(() => {
  return contentType.value === 'article'
    ? {
        ...pageContent.value,
        dateFormatted: data.value?.dateFormatted,
        minutesToRead: data.value?.minutesToRead,
        title: content.value.title,
        shortDescription: content.value.shortDescription
      }
    : null
})

const campaign = computed(() => {
  return contentType.value === 'campaign' ? pageContent.value : null
})

const caseStudy = computed(() => {
  return contentType.value === 'caseStudy' ? pageContent.value : null
})

const pageSeoData = computed(() => {
  return {
    url: pageRoute.value.route,
    title: data.value?.pageData?.seo.title || 'title',
    description: data.value?.pageData?.seo.description
  }
})

const socialItems = computed(() => {
  const url = route.fullPath
  const title = content.value.title || 'title'
  const description = data.value?.pageData?.seo.description

  const networks = {
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${url}&title=${title}&description=${description}`,
    twitter: `https://twitter.com/intent/tweet?text=${title}&url=${url}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`
  }

  return isScreenLarge.value
    ? [
        {
          iconName: 'Facebook',
          text: `Facebook ${labels.value.opensInNewWindow}`,
          url: networks.facebook
        },
        {
          iconName: 'Linkedin',
          text: `Linkedin ${labels.value.opensInNewWindow}`,
          url: networks.linkedin
        },
        {
          iconName: 'Twitter',
          text: `Twitter ${labels.value.opensInNewWindow}`,
          url: networks.twitter
        }
      ]
    : []
})

const headerTag = computed(() => {
  return content.value?.articleType?.name
})

const headerImage = computed(() => {
  return data.value?.headerImage
})

const sidebarTags = computed(() => {
  const tagsData = [content.value?.articleType]
    .concat(content.value?.topicsCollection)
    .concat(content.value?.productsCollection)
    .concat(content.value?.industriesCollection)
    .filter(Boolean)

  return tagsData.map((tag) => {
    const filterKey = knowledgeHubFilterMap[tag.contentType]
    // using id or sys.id for compatibily between not mapped global data
    const filterValue = tag.id || tag.sys?.id
    return {
      name: tag.name,
      link: `${articlesPageRoute.value}?${filterKey}=${filterValue}`
    }
  })
})

const handleScrollToTop = () => {
  if (typeof window !== 'undefined') {
    window.scrollTo({
      left: 0,
      top: 0,
      behavior: 'smooth'
    })
  }
}

const updateFormState = () => {
  handleScrollToTop()
  isFormSubmitStateSuccess.value = true
}

await onAfterAdyenData(data.value)
</script>
